/**
 * @description pure functions that can be used to compare different data types for sorting
 */

import { DateTime } from 'luxon';

// Constant for sorting in ascending order
export const ASC = 'asc';

// Constant for sorting in descending order
export const DESC = 'desc';

/**
 * Gets the sort order numeric value based on if the sort order is ascending or descending
 * @param {string} order - sort order enumerated type
 * @returns {number} - numeric sort order value
 */
export function getNumericOrder (order) {
  const isGreaterThan = order === ASC ? 1 : -1;
  const isLessThan = order === ASC ? -1 : 1;

  return [isGreaterThan, isLessThan];
}

/**
 * Sort order function for comparing ISO date values
 * @param {string} valueA - date value A
 * @param {string} valueB - date value B
 * @param {string} order - sort order
 * @returns {number} - sort results
 */
export function compareIsoDates (valueA, valueB, order) {
  // Get numeric sort order
  const [isGreaterThan, isLessThan] = getNumericOrder(order);

  // Covert values to luxon dates
  const dateA = DateTime.fromISO(valueA);
  const dateB = DateTime.fromISO(valueB);

  // If the dates are equal down the millisecond, return equal value
  if (dateA.startOf('millisecond') === dateB.startOf('millisecond')) {
    return 0;
  }

  // Compare the dates and return the appropriate sort results
  return dateA > dateB ? isGreaterThan : isLessThan;
}

/**
 * Sort order function for comparing numbers
 * @param {string | number} valueA - value A
 * @param {string | number} valueB - value B
 * @param {string} order - sort order
 * @returns {number} - sort results
 */
export function compareNumbers (valueA, valueB, order) {
  // Get numeric sort order
  const [isGreaterThan, isLessThan] = getNumericOrder(order);

  // Convert the values to numbers, if they're currently string types
  const numA = typeof valueA === 'string' ? parseFloat(valueA) : valueA;
  const numB = typeof valueB === 'string' ? parseFloat(valueB) : valueB;

  // If they're equal, return equal value
  if (numA === numB) {
    return 0;
  }

  // Compare the numbers and return the appropriate sort results
  return numA > numB ? isGreaterThan : isLessThan;
}

/**
 * Sort order function for comparing strings (case insensitive)
 * @param {string} valueA - value A
 * @param {string} valueB - value B
 * @param {string} order - sort order
 * @returns {number} - sort results
 */
export function compareStrings (valueA, valueB, order) {
  // Get numeric sort order
  const [isGreaterThan, isLessThan] = getNumericOrder(order);

  // Convert the values to strings (in case they're actually numbers or something else) and trim whitespace
  const strA = String(valueA).trim();
  const strB = String(valueB).trim();

  // Compare the strings (case insensitive)
  const result = strA.localeCompare(strB, 'en', { sensitivity: 'base', caseFirst: false });

  // If they're equal, return equal value
  if (result === 0) {
    return 0;
  }

  // Based on the compare results, return the appropriate sort result value
  return result > 0 ? isGreaterThan : isLessThan;
}

/**
 * A default compare function that inspects the value type and attempts to sort them based on if they're numbers or strings
 * @param {string | number} valueA - value A
 * @param {string | number} valueB - value B
 * @param {string} order - sort order
 * @returns {number} - sort results
 */
export function defaultCompare (valueA, valueB, order) {
  // Determine if the values are numeric
  // Based off of https://stackoverflow.com/questions/9716468/pure-javascript-a-function-like-jquerys-isnumeric
  const isValueANumeric = typeof valueA === 'number' || (!Number.isNaN(parseFloat(valueA)) && isFinite(valueA));
  const isValueBNumeric = typeof valueB === 'number' || (!Number.isNaN(parseFloat(valueB)) && isFinite(valueB));

  // If both value A and value B are numeric, compare them as numbers
  // Otherwise, compare them as strings
  if (isValueANumeric && isValueBNumeric) {
    return compareNumbers(valueA, valueB, order);
  }

  return compareStrings(valueA, valueB, order);
}
